import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, OnChanges, SimpleChanges,
  ViewChild, ElementRef, ChangeDetectorRef, NgZone, HostListener,
} from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BusyIndicatorComponent } from 'src/app/views/shared/busy-indicator/busy-indicator.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subscription, firstValueFrom } from 'rxjs';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { skip } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { CopilotService } from '../services/copilot.service';
import { CopilotContextService } from '../services/copilot-context.service';
import { UserDataService } from 'src/app/services/user-data-service';
import { CopilotSettingsService } from 'src/app/services/copilot-settings.service';
import {
  CopilotMode, CopilotSessionInfo, ChatMessage,
  CopilotProposal, CopilotInsight, SseEvent,
  CopilotChoiceOption, CopilotLabel, TransformerSavedEvent, AnalysisCompletePayload,
  CopilotUsageStatus, CopilotUsageLimitError,
} from '../models/copilot.models';

@Component({
  selector: 'app-copilot-panel',
  templateUrl: './copilot-panel.component.html',
  styleUrls: ['./copilot-panel.component.scss'],
  standalone: false,
  providers: [
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { showDelay: 1000, hideDelay: 0, touchendHideDelay: 0 } },
  ],
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(20px)' })),
      ]),
    ]),
  ],
})
export class CopilotPanelComponent implements OnInit, OnDestroy, OnChanges {

  @Input() transformType: number = 7;
  @Input() transformId: string   = '';
  @Input() refineryId: string    = '';
  @Input() transformLabel: string = 'Transformer';
  @Input() transformName: string  = '';
  @Input() currentConfig: any    = {};
  @Input() columnNames: string[] = [];
  @Input() constraints: any      = {};
  @Input() inputCacheKey: string = '';
  @Input() lastRunStats: any     = {};

  /** Emitted when the user approves a proposal — parent applies it to the live config. */
  @Output() configMergeRequested   = new EventEmitter<any>();
  @Output() configReplaceRequested = new EventEmitter<any>();
  /** Emitted during Copilot's work cycle — live preview of rules being built. */
  @Output() configPreviewUpdate    = new EventEmitter<any>();
  /** Emitted when Copilot sends a structured data preview (e.g. contamination rows). */
  @Output() analysisPreviewUpdate  = new EventEmitter<any>();
  /** Emitted when the analyst clicks Run Audit after a completed Copilot build (Flavor 1). */
  @Output() runAuditRequested      = new EventEmitter<void>();

  @ViewChild('chatWindow') chatWindow!: ElementRef<HTMLDivElement>;
  @ViewChild(CdkDrag) cdkDrag?: CdkDrag;

  @HostListener('window:resize')
  onWindowResize(): void {
    this._handleViewportChange();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if ((this.isProcessing || this.isStopping) && this.isExpanded) {
      this.stopProcessing();
    }
  }

  // visualViewport.resize fires for browser zoom steps that don't always trigger window:resize
  private _onVisualViewport = () => this._handleViewportChange();

  private _handleViewportChange(): void {
    // A monitor move (different OS scaling, e.g. 150% -> 100%) changes screen.width/height;
    // a same-monitor browser zoom does not. Only re-baseline on the former — re-baselining
    // on every DPR change would defeat zoom compensation entirely, since zoomFactor would
    // always read back as 1.
    const screenChanged = window.screen.width !== this._baseScreenWidth
      || window.screen.height !== this._baseScreenHeight;
    if (screenChanged) {
      this._baseDpr          = window.devicePixelRatio;
      this._baseScreenWidth  = window.screen.width;
      this._baseScreenHeight = window.screen.height;
    }
    this._applyCssZoomVar();
    this.cdkDrag?.reset();
    if (this.isExpanded) {
      this._restoreSize();
      this.cdr.detectChanges();
    }
  }

  private _applyCssZoomVar(): void {
    (this.el.nativeElement as HTMLElement).style.setProperty('--cp-zoom', String(this.zoomFactor));
  }

  // UI state
  isExpanded    = false;
  isMaximized   = false;   // height ONLY — the vertical resize handle (onResizeStart) toggles this off
  isWidthExpanded = false; // width ONLY — independent of isMaximized so dragging height never touches width
  isProcessing  = false;
  isStopping    = false;
  unreadCount   = 0;

  /** Defaults to hidden until confirmed — the trigger button must never render
   * clickable for an org where Copilot is disabled, even for an instant. */
  copilotOrgEnabled = false;

  /** True once an analysis_preview event has been received — unlocks the Run Audit button. */
  hasAuditableSession      = false;
  lastNavigationHint: import('../models/copilot.models').CopilotNavigationHint | null = null;
  lastAnalysisPreviewData: any = null;

  /** Daily token usage banner state — null/'none' level renders nothing. */
  usageStatus: CopilotUsageStatus | null = null;

  /** Synthetic choice object for the fixed Copilot Dashboard action button. */
  readonly dashboardChoice: CopilotChoiceOption = { label: 'Copilot Dashboard', value: -1 };
  readonly optionLetters = 'ABCDEFGHIJ';

  isOptionChoice(value: number | string): boolean {
    // accept/skip choices show their own label; all other string values use "Option A/B/…"
    return typeof value === 'string' && value !== 'accept' && value !== 'skip';
  }

  hasTierChoices(choices: CopilotChoiceOption[] | undefined): boolean {
    return (choices ?? []).some(c => !this.isOptionChoice(c.value));
  }

  onSummaryClick(): void {
    const hint = this.lastNavigationHint;
    if (!hint) return;
    this.copilotCtx.navigateTo$.next(hint);
  }

  onDashboardClick(): void {
    if (this.lastAnalysisPreviewData) {
      this.analysisPreviewUpdate.emit({ ...this.lastAnalysisPreviewData, triggeredBy: 'button' });
    }
  }

  sessionId: string | null = null;
  mode: CopilotMode        = 'builder';
  messages: ChatMessage[]  = [];
  userInput: string        = '';
  pendingProposal:    CopilotProposal | null = null;
  isApplyingProposal: boolean = false;
  // Unified advisor flow — replaces independent pendingSortedRules / pendingSortReason
  advisorAdvice: any | null = null;

  // Tool activity feed
  activeToolName:  string | null = null;
  activeToolInput: any           = {};

  // Processing timer
  processingSeconds = 0;
  private _timerInterval: ReturnType<typeof setInterval> | null = null;

  private _streamSub: Subscription | null = null;
  private _streamingMessageId: string | null = null;
  private _activeCellId: string | null = null;
  private _queuedMessage: string | null = null;
  private _runStatsReadySub: Subscription | null = null;
  private _dtrRunSub: Subscription | null = null;
  private _depthSub: Subscription | null = null;
  private _resetTransformerSub: Subscription | null = null;
  private _workDepth: number | null = null;

  private static readonly _DEPTH_LABELS: Record<number, string> = {
    0: 'Standard', 1: 'Deep Dive', 2: 'Deepest Dive',
  };

  // Post-save survey
  showSurvey = false;
  surveyQualityRating:      number | null = null;  // overall — how good is the built config
  surveyAccuracyRating:     number | null = null;  // did the extracted values look right
  surveySimplicityRating:   number | null = null;  // simple enough to comfortably maintain
  surveyCopilotSpeedRating: number | null = null;  // how fast Copilot BUILT the config
  surveyDtrSpeedRating:     number | null = null;  // how fast the DTR itself RUNS (Run All)
  surveyFrictionRating:     number | null = null;  // how much back-and-forth it took
  surveyTrustRating:        number | null = null;  // needed to double-check, or took as-is
  surveyFeedbackText:       string = '';            // optional free-form comment — Super Admin review only
  readonly surveyStars = [1, 2, 3, 4, 5];
  private _pendingSavedEvent: TransformerSavedEvent | null = null;
  private _savedEventSub: Subscription | null = null;
  // Most recent REAL (non-cached) DTR Run All time, seconds — null until one occurs.
  // Sourced from copilotCtx.dtrRunMs$, the same stream that drives the DTR Run stat chip.
  private _lastDtrRunSeconds: number | null = null;
  // True only when Copilot has made at least one config change (rules or settings)
  // this page load. Pure Q&A sessions produce nothing worth learning from.
  private _hasCopilotConfigChange = false;

  private static readonly _SURVEY_PREF_KEY = 'copilot_survey_enabled';
  private static readonly _SIZE_PREF_KEY   = 'copilot_panel_size';
  surveyEnabled: boolean = localStorage.getItem(CopilotPanelComponent._SURVEY_PREF_KEY) !== 'false';

  // DTR Copilot Config Analyser — Analysis Survey. Deliberately separate from the
  // build survey above (Architect's decision) — triggered by the analysis flow's
  // "Summarize to Description?" checkpoint choice, not by Save Transformer.
  showAnalysisSurvey = false;
  analysisInsightRating:      number | null = null;  // genuinely useful, not a rules re-listing
  analysisAccuracyRating:     number | null = null;  // findings matched what the analyst already knew
  analysisActionabilityRating: number | null = null; // suggestions were something they could act on
  analysisGoalsUnderstandingRating: number | null = null;  // did Copilot correctly grasp the transformer's goals
  analysisTrustRating:        number | null = null;  // needed to double-check, or took as-is
  analysisFeedbackText:       string = '';
  private _wantsAnalysisDescriptionAppend = false;

  constructor(
    private copilotSvc: CopilotService,
    private copilotCtx: CopilotContextService,
    private userDataSvc: UserDataService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    public copilotSettings: CopilotSettingsService,
    private el: ElementRef,
    private matDialog: MatDialog,
  ) {}

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  ngOnInit(): void {
    this._applyCssZoomVar();
    window.visualViewport?.addEventListener('resize', this._onVisualViewport);
    this._tryRestoreSession();

    this.copilotSvc.getCopilotEnabledStatus(this._orgId).subscribe({
      next: (resp) => { this.copilotOrgEnabled = !!resp?.enabled; },
      error: () => { this.copilotOrgEnabled = false; },
    });

    // Usage bar must reflect today's real usage the moment the panel opens, not
    // only after the first turn's usage_status SSE event (RCA 2026-09-19: before
    // this, a fresh panel showed no bar at all until the Analyst sent a message,
    // even though Settings > Users already showed their real, nonzero usage for
    // today) — reuses the exact same batch-today endpoint that page calls. A
    // later usage_status SSE event (end of the first turn) overwrites this with
    // fresher data; this is purely to avoid a blank gap before that.
    this.copilotSvc.getUsageBatchToday(this._orgId, [this._userId]).subscribe({
      next: (resp) => {
        const today = resp?.[String(this._userId)];
        if (today) this.usageStatus = today;
      },
      error: () => {},
    });

    // "Reset Transformer" wiped the config to blank — any existing Copilot session
    // now refers to rules/state that no longer exist, so drop it rather than let a
    // stale conversation linger with no way to act on it (see the restore-session
    // bad-state screenshot this was introduced to avoid repeating).
    this._resetTransformerSub = this.copilotCtx.resetTransformer$.subscribe(() => {
      this.resetSession();
    });

    // Signal the Flask stats waiter immediately when the DTR run completes — bypasses
    // Angular CD timing that makes ngOnChanges unreliable for the 90s waiter window.
    this._runStatsReadySub = this.copilotCtx.runStatsReady$.subscribe(runStats => {
      if (!this.sessionId) return;
      this.copilotSvc.refreshContext(this.sessionId, {
        userId:        this._userId,
        currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
        columnNames:   this.columnNames,
        inputCacheKey: this.inputCacheKey,
        lastRunStats:  runStats,
      }).subscribe({
        next: (resp) => {
          if (resp?.advisorFlow && this.transformId && this.isExpanded && !this.isProcessing) {
            const _hasPending = this.messages.some(m => m.choices && m.choices.length > 0 && !m.choiceSelected);
            if (!_hasPending && !this.advisorAdvice) {
              this._showAdvisorAdvice(resp.advisorFlow);
            }
          }
        },
      });
    });

    // When the analyst saves the transformer, optionally show the post-save survey.
    // No-ops if no session is active or the analyst has disabled the survey.
    this._dtrRunSub = this.copilotCtx.dtrRunMs$.subscribe(ms => {
      // Track the most recent REAL (non-cached) run for the work-complete payload —
      // a cache hit (-1) leaves the last measured value in place rather than clearing it.
      if (ms >= 0) {
        this._lastDtrRunSeconds = ms / 1000;
      }
      const lastAssistant = [...this.messages].reverse().find(m => m.role === 'assistant');
      if (!lastAssistant) return;
      const label = ms < 0 ? '⏱ DTR Cached' : `⏱ DTR ${(ms / 1000).toFixed(1)}s`;
      if (lastAssistant.content.includes('\n⏱')) {
        lastAssistant.content = lastAssistant.content.replace(/\n⏱.*$/, `\n${label}`);
      } else {
        lastAssistant.content += `\n${label}`;
      }
      this.cdr.detectChanges();
    });

    this._depthSub = this.copilotSettings.depth$.pipe(skip(1)).subscribe(depth => {
      // Always clear any existing depth warning first.
      this.messages = this.messages.filter(m => m.subType !== 'depth_warning');

      // Analyst reverted to the depth that was used for the current work — no warning needed.
      if (this._workDepth !== null && depth === this._workDepth) {
        this.cdr.detectChanges();
        return;
      }

      const hasActiveWork = this.messages.some(m => m.proposal || (m.choices && m.choices.length > 0));
      if (hasActiveWork) {
        const label = CopilotPanelComponent._DEPTH_LABELS[depth] ?? `Depth ${depth}`;
        this._addSystemMessage(
          `Depth changed to ${label} — please proceed with current session or start a new one.`,
          'depth_warning'
        );
      }
    });

    this._savedEventSub = this.copilotCtx.transformerSaved$.subscribe(event => {
      if (!this.sessionId || !this._hasCopilotConfigChange) return;
      this._pendingSavedEvent = event;
      if (this.surveyEnabled) {
        this.showSurvey               = true;
        this.surveyQualityRating      = null;
        this.surveyAccuracyRating     = null;
        this.surveySimplicityRating   = null;
        this.surveyCopilotSpeedRating = null;
        this.surveyDtrSpeedRating     = null;
        this.surveyFrictionRating     = null;
        this.surveyTrustRating        = null;
        this.surveyFeedbackText       = '';
        if (!this.isExpanded) this.isExpanded = true;
        // The survey footer takes its own space from the same flex column the
        // chat window scrolls in — without re-scrolling, the chat window shrinks
        // to make room but keeps its old scroll position, leaving the tail of
        // the last message hidden behind the newly-added survey.
        this._scrollToBottom();
      } else {
        // Survey disabled — still flush work-complete silently with no ratings
        this._completeSurvey(false);
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    window.visualViewport?.removeEventListener('resize', this._onVisualViewport);
    this._stopTimer();
    this._streamSub?.unsubscribe();
    this._savedEventSub?.unsubscribe();
    this._runStatsReadySub?.unsubscribe();
    this._dtrRunSub?.unsubscribe();
    this._depthSub?.unsubscribe();
    this._resetTransformerSub?.unsubscribe();
    this.copilotSvc.closeStream();
    // Delete the backend session so the next visit starts with a clean window.
    if (this.sessionId) {
      this.copilotSvc.deleteSession(this.sessionId, this._userId).subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep the Copilot in sync when the transformer config changes
    if (this.sessionId && (changes['currentConfig'] || changes['columnNames'] || changes['constraints'] || changes['inputCacheKey'] || changes['lastRunStats'])) {
      this.copilotSvc.refreshContext(this.sessionId, {
        userId: this._userId,
        currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
        columnNames: this.columnNames,
        inputCacheKey: this.inputCacheKey,
        lastRunStats: this.lastRunStats,
      }).subscribe();
    }
  }

  // ------------------------------------------------------------------
  // Panel toggle
  // ------------------------------------------------------------------

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.unreadCount = 0;
      this._restoreSize();
      if (!this.sessionId) {
        this._startSession();
      }
      setTimeout(() => this._scrollToBottom(), 100);
    }
  }

  // Represents "100% browser zoom" on whichever monitor the panel currently lives on.
  // Used to derive how far the current DPR has moved from baseline, i.e. the browser zoom
  // factor. NOT readonly/set-once: window.devicePixelRatio conflates browser zoom with the
  // OS's per-monitor DPI scaling, and this component is a root-level singleton that survives
  // the window being dragged to a different monitor — so the baseline must be re-captured
  // whenever the monitor (not just the zoom level) changes, or a genuine monitor move gets
  // misread as a zoom change and every dimension scales in the wrong direction.
  private _baseDpr: number = window.devicePixelRatio;
  // screen.width/height are CSS-pixel screen dimensions that change with the current
  // monitor's OS scaling, but do NOT change from browser zoom alone — that difference is
  // what lets _handleViewportChange tell a monitor move apart from a same-monitor zoom.
  private _baseScreenWidth: number  = window.screen.width;
  private _baseScreenHeight: number = window.screen.height;

  private _manualHeight: number | null = null;

  /** Zoom factor relative to baseline DPR: 1.0 at 100% zoom, 1.25 at 125%, etc. */
  get zoomFactor(): number {
    return window.devicePixelRatio / this._baseDpr;
  }

  /** CSS width that counteracts browser zoom so the panel stays the same physical
   * size. Keyed on isWidthExpanded, NOT isMaximized — deliberately independent so
   * the vertical resize handle (onResizeStart, which turns isMaximized off to hand
   * height control to the Analyst) never touches width. Viewport-capped so it
   * can't overflow a narrow browser window. */
  get panelWidthStyle(): string {
    const baseWidth = this.isWidthExpanded ? 840 : 420;
    const minWidth  = this.isWidthExpanded ? 600 : 300;
    const cssWidth  = Math.round(baseWidth / this.zoomFactor);
    const viewportCap = window.innerWidth - 40;
    return `${Math.max(minWidth, Math.min(cssWidth, baseWidth, viewportCap))}px`;
  }

  get cardHeightStyle(): string | null {
    if (this.isMaximized || this._manualHeight === null) return null;
    return `${this._manualHeight}px`;
  }

  /** The one "expand" button toggles both dimensions together; the vertical
   * resize handle (onResizeStart) only ever touches isMaximized, never this. */
  toggleSize(): void {
    this.isMaximized     = !this.isMaximized;
    this.isWidthExpanded = this.isMaximized;
    if (this.isMaximized) this._manualHeight = null;
    this._saveSize();
  }

  private _saveSize(): void {
    try {
      localStorage.setItem(CopilotPanelComponent._SIZE_PREF_KEY, JSON.stringify({
        isMaximized:      this.isMaximized,
        isWidthExpanded:  this.isWidthExpanded,
        manualHeight:     this._manualHeight,
        viewportHeight:   window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
      }));
    } catch { /* ignore */ }
  }

  private _restoreSize(): void {
    try {
      const raw = localStorage.getItem(CopilotPanelComponent._SIZE_PREF_KEY);
      if (!raw) return;
      const { isMaximized, isWidthExpanded, manualHeight, viewportHeight, devicePixelRatio: savedDpr } = JSON.parse(raw);
      // Reset if viewport shrank/grew by >30px OR if zoom level (DPR) changed at all
      const viewportChanged = Math.abs(window.innerHeight - (viewportHeight ?? 0)) > 30;
      const zoomChanged     = Math.abs(window.devicePixelRatio - (savedDpr ?? 1)) > 0.05;
      if (viewportChanged || zoomChanged) {
        this.isMaximized     = false;
        this.isWidthExpanded = false;
        this._manualHeight   = null;
      } else {
        this.isMaximized     = !!isMaximized;
        this.isWidthExpanded = !!isWidthExpanded;
        // Clamp manual height to valid range for the current viewport
        this._manualHeight = typeof manualHeight === 'number'
          ? Math.min(manualHeight, window.innerHeight - 168)
          : null;
      }
    } catch { /* ignore corrupt entry */ }
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    const card = this.cdkDrag?.element?.nativeElement as HTMLElement | undefined;
    if (!card) return;
    const startY      = event.clientY;
    const startHeight = card.getBoundingClientRect().height;
    this.isMaximized  = false;

    const onMove = (e: MouseEvent) => {
      // Handle is at the bottom of a panel that grows upward, so dragging up increases height.
      const newHeight = Math.max(300, Math.min(startHeight - (e.clientY - startY), window.innerHeight - 168));
      this._manualHeight = newHeight;
      this.cdr.detectChanges();
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this._saveSize();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // ------------------------------------------------------------------
  // Session management
  // ------------------------------------------------------------------

  private _tryRestoreSession(): void {
    this.copilotSvc.restoreSession(this._userId, this.transformId).subscribe({
      next: (result) => {
        if (result.found && result.sessionId) {
          // Staleness check: if the actual config has been reset to zero rules but
          // the session expected rules to exist, the session is stale. Discard it
          // and start a clean session — Copilot must fly with the actual cockpit state.
          const actualRuleCount  = (this.currentConfig?.rules as any[])?.length ?? 0;
          const sessionRuleCount = result.sandbox?.ruleCount ?? 0;
          if (actualRuleCount === 0 && sessionRuleCount > 0) {
            this.copilotSvc.deleteSession(result.sessionId, this._userId).subscribe();
            this._startSession();
            return;
          }

          this.sessionId = result.sessionId;
          this.mode = result.mode ?? 'builder';
          // Rebuild messages from saved turns (skip internal system triggers)
          const HIDDEN_TRIGGERS = ['__greet__', 'Hello! I just opened you. Please greet me and summarise what you see.'];
          (result.turns ?? []).filter(t => !HIDDEN_TRIGGERS.includes(t.content)).forEach(t => {
            this.messages.push({
              id: uuidv4(),
              role: t.role as any,
              content: t.content,
              isStreaming: false,
              timestamp: new Date(t.timestamp),
            });
          });
          if (result.sandbox?.hasProposal) {
            this._addSystemMessage(
              'I have a proposal ready for you. Say "show proposal" to review it, or continue working.'
            );
          }
          // Sync current inputCacheKey — the restored session may have a stale key.
          this._syncContextToSession();
          this.cdr.detectChanges();
        }
      },
      error: () => { /* no session — will create on first open */ },
    });
  }

  private _startSession(): void {
    this.copilotSvc.startSession({
      userId:         this._userId,
      orgId:          this._orgId,
      refineryId:     this.refineryId,
      transformId:    this.transformId,
      transformType:  this.transformType,
      transformLabel: this.transformLabel,
      transformName:  this.transformName,
      columnNames:  this.columnNames,
      constraints:  this.constraints,
      currentConfig: this.currentConfig,
      inputCacheKey: this.inputCacheKey,
    }).subscribe({
      next: (info: CopilotSessionInfo) => {
        this.sessionId = info.sessionId;
        this.mode      = info.mode;
        this._syncContextToSession();
        // Apply persisted mode preference if it differs from the server default.
        const savedMode = this.copilotSettings.mode;
        if (savedMode !== this.mode) {
          this.copilotSvc.setMode(this.sessionId!, this._userId, savedMode).subscribe({
            next: () => { this.mode = savedMode; },
          });
        }
      },
      error: (err) => {
        this._addSystemMessage('Failed to start Copilot session. Please try again.');
        console.error('Copilot start failed:', err);
      },
    });
  }

  private _syncContextToSession(): void {
    if (!this.sessionId) return;
    this.copilotSvc.refreshContext(this.sessionId, {
      userId:        this._userId,
      currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
      columnNames:   this.columnNames,
      constraints:   this.constraints,
      inputCacheKey: this.inputCacheKey,
      lastRunStats:  this.lastRunStats,
    }).subscribe();
  }

  private _sendGreeting(): void {
    this._sendToBackend('__greet__');
  }

  // ------------------------------------------------------------------
  // Messaging
  // ------------------------------------------------------------------

  stopProcessing(): void {
    if (!this.isProcessing && !this.isStopping) return;

    if (this.isStopping) {
      // Second click — force close immediately without waiting for the server.
      this._streamSub?.unsubscribe();
      this.copilotSvc.closeStream();
      this._stopTimer();
      const _stoppedMsgId = this._streamingMessageId;
      if (_stoppedMsgId) this._finaliseStreamingMessage(_stoppedMsgId);
      this.isProcessing   = false;
      this.isStopping     = false;
      this.activeToolName = null;
      if (_stoppedMsgId) this._addStopMessage(_stoppedMsgId);
      this.cdr.detectChanges();
      return;
    }

    // First click — graceful stop: signal the server, keep the stream open.
    // The server finishes the current tool call, then closes the stream.
    // _onStreamComplete() fires when the stream actually closes.
    this.isStopping     = true;
    this.activeToolName = null;
    if (this.sessionId) {
      this.copilotSvc.abort(this.sessionId, this._userId).subscribe();
    }
    this.cdr.detectChanges();
  }

  private _onStreamComplete(assistantMsgId: string): void {
    // Resolve before _finaliseStreamingMessage clears _streamingMessageId to null —
    // see that method's own comment for why this must be the live id, not the stale
    // one captured when the stream started.
    const finalMsgId = this._streamingMessageId ?? assistantMsgId;
    this._stopTimer();
    this._finaliseStreamingMessage(finalMsgId);
    const wasStopping   = this.isStopping;
    this.isProcessing   = false;
    this.isStopping     = false;
    this.activeToolName = null;
    if (this._lastStreamWasAnalysis) {
      this._lastStreamWasAnalysis = false;
      if (!wasStopping) this.showSummarizeAnalysisBtn = true;
    }
    this.cdr.detectChanges();
    this._scrollToBottom();
    if (wasStopping) {
      this._addStopMessage(finalMsgId);
    } else if (this._queuedMessage) {
      const queued        = this._queuedMessage;
      this._queuedMessage = null;
      this._appendMessage('user', queued);
      this._sendToBackend(queued);
    }
  }

  send(): void {
    const msg = this.userInput.trim();
    if (!msg || !this.sessionId) return;

    if (this.isProcessing) {
      // Queue one message — sent automatically when current response finishes.
      this._queuedMessage = msg;
      this.userInput = '';
      this._addSystemMessage('Message queued — will send when Copilot finishes.');
      return;
    }

    this.userInput = '';
    this._workDepth = this.copilotSettings.depth;
    this._appendMessage('user', msg);
    this._sendToBackend(msg);
  }

  private _sendToBackend(message: string): void {
    if (!this.sessionId) return;

    this.isProcessing = true;
    this.activeToolName = null;
    this.processingSeconds = 0;
    this._timerInterval = setInterval(() => {
      this.processingSeconds++;
      this.cdr.detectChanges();
    }, 1000);

    // Create an empty assistant message that will be populated by streaming
    const assistantMsgId = uuidv4();
    this._streamingMessageId = assistantMsgId;
    this._activeCellId       = assistantMsgId;
    this.messages.push({
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      timestamp: new Date(),
      toolActivity: [],
    });

    this._streamSub?.unsubscribe();
    this._streamSub = this.copilotSvc.sendMessage(
      this.sessionId!, this._userId, message,
      this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
    ).subscribe({
      next: (event: SseEvent) => this._handleSseEvent(event, assistantMsgId),
      error: (err) => {
        this._stopTimer();
        const wasStopping = this.isStopping;
        // err.error carries the server's actual JSON body (e.g. the 'busy' message,
        // or the usage-limit block message below) — err.message is just Angular's
        // generic HttpErrorResponse text ("Http failure response for ...") and was
        // silently swallowing the real message wherever this fired.
        const serverMessage = err?.error?.message ?? err?.message ?? err;
        if (err?.error?.error === 'usage_limit_exceeded') {
          this.usageStatus = {
            usedCredits:  err.error.creditsUsed,
            limitCredits: err.error.creditsLimit,
            usagePercent: err.error.usagePercent,
            level:        'red',
          } as CopilotUsageStatus;
        }
        this._finaliseStreamingMessage(assistantMsgId, wasStopping ? '' : `Error: ${serverMessage}`);
        this.isProcessing = false;
        this.isStopping   = false;
        this.cdr.detectChanges();
        if (wasStopping) {
          this._addStopMessage(assistantMsgId);
        }
      },
      complete: () => this._onStreamComplete(assistantMsgId),
    });
  }

  private _handleSseEvent(event: SseEvent, assistantMsgId: string): void {
    const activeCellId = this._activeCellId ?? assistantMsgId;
    const msg = this.messages.find(m => m.id === activeCellId);

    switch (event.type) {
      case 'token':
        if (msg) {
          msg.content += event.data as string;
          this._scrollToBottom();
        }
        break;

      case 'tool_start':
        this.activeToolName  = (event.data as any)?.name  ?? null;
        this.activeToolInput = (event.data as any)?.input ?? {};
        if (msg) {
          if (!msg.toolActivity) msg.toolActivity = [];
          const lastActivity = msg.toolActivity[msg.toolActivity.length - 1];
          if (lastActivity && lastActivity.name === this.activeToolName) {
            // Same tool called again right away (e.g. one build_rule call per staged rule) —
            // collapse into the existing chip instead of stacking a new one per call. The
            // analyst gains nothing from seeing "build rule" repeated a dozen times in a row.
            lastActivity.phase = 'running';
            lastActivity.input = this.activeToolInput;
          } else {
            msg.toolActivity.push({
              name:  this.activeToolName!,
              phase: 'running',
              input: this.activeToolInput,
            });
          }
        }
        break;

      case 'tool_result':
        this.activeToolName  = null;
        this.activeToolInput = {};
        const toolName = (event.data as any)?.name;
        const result   = (event.data as any)?.result;
        if (msg?.toolActivity) {
          const activity = msg.toolActivity.find(t => t.name === toolName && t.phase === 'running');
          if (activity) activity.phase = 'done';
        }
        // A real DTR execution just produced real stats — Dashboard/Summary/Audit have
        // something to show. Previously this only ever got set by the Advisor's
        // analysis_preview event, so a BPM (or any other) build that never goes through
        // Advisor could complete with real results and still show no action buttons at
        // all, regardless of msgHasAnalysisActivity's own answer.
        if (toolName === 'run_transformer' && result?.success) {
          this.hasAuditableSession = true;
        }
        // update_description: Copilot offered (via a checkpoint choice, never
        // unprompted) to summarize the conversation's requirements/decisions into
        // the Description field and the Analyst agreed. Reuses the exact same
        // analystDescriptionAppend contract the two fixed end-of-session survey
        // flows already use (see work_complete/analysis-complete below) — this is
        // just a second, LLM-initiated trigger for the same Angular-side handling.
        if (toolName === 'update_description' && result?.analystDescriptionAppend) {
          // Must be nested under `config` — app.component.html's (configPreviewUpdate)
          // binding reads $event.config; emitting analystDescriptionAppend at the top
          // level (as before) silently dropped it (config was undefined downstream).
          this.configPreviewUpdate.emit({ config: { analystDescriptionAppend: result.analystDescriptionAppend } });
        }
        // If a proposal came back, capture it
        if (toolName === 'apply_rules_proposal' && result?.status === 'proposal_ready') {
          this.pendingProposal = {
            ruleCount:           result.rule_count,
            rationale:           result.rationale,
            mergeRecommendation: result.merge_recommendation,
            mergeRationale:      result.merge_rationale,
            rulesSummary:        result.rules_summary ?? [],
            source:              'builder',
          };
          if (msg) msg.proposal = this.pendingProposal;
        }
        // analyze_and_run stores proposals directly — auto-apply, no approval card needed.
        // Use replace: the proposal already contains the full rule set (existing + new),
        // so merge mode would double-offset the new rule indices.
        if (toolName === 'analyze_and_run' && result?.proposal_stored) {
          this.pendingProposal = {
            ruleCount:           result.rules_built ?? 0,
            rationale:           '',
            mergeRecommendation: 'replace',
            mergeRationale:      '',
            rulesSummary:        [],
            source:              'builder',
          };
          this.onApproveProposal('replace');
        }
        // Advisor full analysis — proposed_config already incorporates existing rules
        // (_build_phase writes into current_config first). Merge is not valid here —
        // it would double all rules. Only Replace is offered.
        if (toolName === 'run_full_analysis' && result?.status === 'proposal_ready') {
          this.pendingProposal = {
            ruleCount:           result.rule_count ?? 0,
            rationale:           '',
            mergeRecommendation: 'replace',
            mergeRationale:      '',
            rulesSummary:        [],
            source:              'run_full_analysis',
          };
          if (msg) msg.proposal = this.pendingProposal;
        }
        // Navigation hint from checkpoint — store on the message for the button to emit.
        // Must ALSO set lastNavigationHint: that's the field the Summary button's *ngIf
        // actually checks (the 'choice' event path below sets it, but plain checkpoints
        // — the only path BPM's fully-automatic flow ever uses — never went through that
        // path, so the button never appeared no matter what navigation data arrived here).
        if (result?.navigation && msg) {
          msg.navigation = result.navigation;
          this.lastNavigationHint = result.navigation;
        }
        break;

      case 'sample_range_update':
        this.copilotCtx.applySampleRange(event.data);
        break;

      case 'usage_status':
        this.usageStatus = event.data as CopilotUsageStatus;
        break;

      case 'config_update':
        // Copilot wrote and ran a rule or applied a setting — config was changed.
        this._hasCopilotConfigChange = true;
        // skipRun: this push is a still-staging batch rule (BPM defer_run), not a validated
        // result — the receiving component should update its display but must not launch a
        // live DTR run for it. The marker travels as a property ON the config object itself
        // (_copilot_skip_preview_run) rather than a sibling field on the SSE event — the
        // backend relay only forwards `data`, dropping any sibling keys.
        const _skipRun = !!(event.data as any)?._copilot_skip_preview_run;
        this.configPreviewUpdate.emit({ config: event.data, skipRun: _skipRun });
        break;

      case 'copilot_score':
        this.ngZone.run(() => this.copilotCtx.emitBpmScore(event.data));
        break;

      case 'analysis_preview':
        this.lastAnalysisPreviewData = event.data;
        this.analysisPreviewUpdate.emit(event.data);
        this.hasAuditableSession = true;
        break;

      case 'insight':
        const insight = event.data as CopilotInsight;
        this._appendInsightMessage(insight);
        if (!this.isExpanded) this.unreadCount++;
        break;

      case 'choice':
        if (msg) {
          const cd = event.data as { message: string; choices: CopilotChoiceOption[]; recommendedValue?: number | string; labels?: CopilotLabel[]; navigation?: any };
          // Do NOT set msg.content here — ai_gateway.py already sent this same text (plus
          // any labels prefix) as a 'token' event just before 'choice', appended via +=
          // (see the 'token' case above). Overwriting it here wiped out whatever had
          // already accumulated in this bubble — confirmed live, 2026-09-13, as the direct
          // cause of a multi-cell build's earlier cells' text disappearing the moment a
          // final choice/decision was offered.
          msg.choices = cd.choices;
          if (cd.recommendedValue != null) msg.recommendedChoice = cd.recommendedValue;
          if (cd.labels && cd.labels.length > 0) msg.labels = cd.labels;
          if (cd.navigation) {
            msg.navigation = cd.navigation;
            this.lastNavigationHint = cd.navigation;
          }
        }
        break;

      case 'cell_boundary':
        // Finalise the current streaming bubble and open a fresh one for the next cell.
        if (msg) {
          msg.isStreaming  = false;
          msg.toolActivity = msg.toolActivity ?? [];
        }
        this._streamingMessageId = null;
        const nextCellId = uuidv4();
        this._streamingMessageId = nextCellId;
        this._activeCellId       = nextCellId;
        this.messages.push({
          id: nextCellId,
          role: 'assistant',
          content: '',
          isStreaming: true,
          timestamp: new Date(),
          toolActivity: [],
        });
        this._scrollToBottom();
        break;

      case 'navigation':
        this.lastNavigationHint = event.data;
        break;

      case 'advisor_flow':
        if (event.data && !this.advisorAdvice) {
          this._showAdvisorAdvice(event.data);
        }
        break;

      case 'error':
        if (msg && event.data) {
          msg.content += `\n\n⚠️ ${event.data}`;
        }
        break;
    }

    this.cdr.detectChanges();
  }

  private _finaliseStreamingMessage(msgId: string, errorContent?: string): void {
    // Prefer the LIVE _streamingMessageId over the passed-in id: every 'complete'/'error'
    // subscribe callback closes over the id captured when the stream started (see send()
    // and its siblings), which cell_boundary (see _handleSseEvent) reassigns to a fresh
    // message per cell in a multi-cell conversation — but never updates that stale
    // closure variable. Confirmed live, 2026-09-13: the true last cell's isStreaming flag
    // was never cleared, which broke lastAssistantMessageId (skips streaming messages)
    // and hid the whole action-button row (Dashboard/Summary/Audit) on the correct
    // message. stopProcessing()'s own force-close path already does this correctly
    // (reads this._streamingMessageId directly) — this makes every other call site match.
    const targetId = this._streamingMessageId ?? msgId;
    const msg = this.messages.find(m => m.id === targetId);
    if (msg) {
      msg.isStreaming = false;
      if (errorContent) msg.content = errorContent;
    }
    this._streamingMessageId = null;
  }

  // ------------------------------------------------------------------
  // Proposal handling
  // ------------------------------------------------------------------

  onTierChoice(msg: ChatMessage, choice: CopilotChoiceOption): void {
    if (msg.choiceSelected || !this.sessionId || this.isProcessing) return;
    msg.choiceSelected = true;

    // String-valued choices (Option A/B/C from checkpoint) are sent back to the LLM
    // as a regular user message so the LLM can execute the chosen approach.
    // Numeric choices (tier selections) go directly to the dedicated send_choice endpoint.
    if (typeof choice.value === 'string') {
      // Accept/Skip — send the label directly so the backend prompt recognises "Accept"/"Skip".
      // Option A/B — send "Option A", "Option B", etc. so the backend knows which was chosen.
      let label: string;
      if (choice.value === 'accept' || choice.value === 'skip') {
        label = choice.label;
      } else {
        const i = (msg.choices ?? []).indexOf(choice);
        label = i >= 0 ? `Option ${this.optionLetters[i]}` : choice.label;
      }
      this._appendMessage('user', label);
      this._sendToBackend(label);
      return;
    }

    this._workDepth = this.copilotSettings.depth;

    this.isProcessing        = true;
    this.activeToolName      = null;
    this.processingSeconds   = 0;
    this._timerInterval = setInterval(() => {
      this.processingSeconds++;
      this.cdr.detectChanges();
    }, 1000);

    const assistantMsgId = uuidv4();
    this._streamingMessageId = assistantMsgId;
    this._activeCellId       = assistantMsgId;
    this.messages.push({
      id: assistantMsgId, role: 'assistant', content: '',
      isStreaming: true, timestamp: new Date(), toolActivity: [],
    });

    this._streamSub?.unsubscribe();
    const liveConfig = this.copilotCtx.getLiveConfig?.() ?? this.currentConfig;
    this._streamSub = this.copilotSvc.sendChoice(
      this.sessionId!, this._userId, choice.value, liveConfig,
      this.copilotSettings.depth,
    ).subscribe({
      next:     (event: SseEvent) => this._handleSseEvent(event, assistantMsgId),
      error:    (err) => {
        this._stopTimer();
        const wasStopping = this.isStopping;
        this._finaliseStreamingMessage(assistantMsgId, wasStopping ? '' : `Error: ${err?.error?.message ?? err?.message ?? err}`);
        this.isProcessing = false;
        this.isStopping   = false;
        this.cdr.detectChanges();
        if (wasStopping) {
          this._addStopMessage(assistantMsgId);
        }
      },
      complete: () => this._onStreamComplete(assistantMsgId),
    });
  }

  async onApproveProposal(applyMode: 'replace' | 'merge'): Promise<void> {
    console.log('[Copilot] onApproveProposal', applyMode, 'sessionId:', this.sessionId, 'pendingProposal:', this.pendingProposal);
    if (!this.sessionId || !this.pendingProposal) {
      console.warn('[Copilot] onApproveProposal bailed — sessionId or pendingProposal missing');
      return;
    }

    this.isApplyingProposal = true;
    this.cdr.detectChanges();

    let result: any;
    try {
      result = await firstValueFrom(this.copilotSvc.approveProposal(this.sessionId, this._userId, applyMode));
    } catch {
      this.isApplyingProposal = false;
      this.cdr.detectChanges();
      this._addSystemMessage('Failed to apply proposal. Please try again.');
      return;
    }

    console.log('[Copilot] approveProposal result:', result);
    if (result.status !== 'ok') {
      this.isApplyingProposal = false;
      this.cdr.detectChanges();
      return;
    }

    console.log('[Copilot] emitting config, rules:', result.finalConfig?.rules?.length);
    this._hasCopilotConfigChange = true;
    if (applyMode === 'replace') {
      this.configReplaceRequested.emit(result.finalConfig);
    } else {
      this.configMergeRequested.emit(result.finalConfig);
    }
    this.pendingProposal = null;

    if (this.mode === 'advisor') {
      this._addSystemMessage(
        "Changes applied — you're still in Advisor mode. Switch to Builder to continue building.",
        'advisor_info'
      );
    }

    // Auto-run DTR so analyst sees results immediately after applying the proposal.
    if (this.copilotCtx.runForAnalysis) {
      this.activeToolName = 'dtr_run';
      this.cdr.detectChanges();
      try {
        await this.copilotCtx.runForAnalysis();
        await firstValueFrom(this.copilotSvc.refreshContext(this.sessionId!, {
          userId:        this._userId,
          currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
          columnNames:   this.columnNames,
          inputCacheKey: this.inputCacheKey,
          lastRunStats:  this.copilotCtx.context$.value?.lastRunStats,
        })).catch(() => {});
      } catch { /* proceed even if auto-run fails */ }
      this.activeToolName = null;
    }

    this.isApplyingProposal = false;
    this.cdr.detectChanges();

    // If the analyst typed a message while the proposal was showing, send it now.
    if (this.userInput.trim()) {
      setTimeout(() => this.send(), 0);
    }
  }

  async onRunFullAnalysis(): Promise<void> {
    if (!this.sessionId || this.isProcessing) return;

    this.isProcessing      = true;
    this.activeToolName    = null;
    this.processingSeconds = 0;
    this._timerInterval    = setInterval(() => { this.processingSeconds++; this.cdr.detectChanges(); }, 1000);

    // Step 1: Run DTR with runAll = true to get fresh hit stats before analysis
    if (this.copilotCtx.runForAnalysis) {
      this.activeToolName = 'dtr_run';
      this.cdr.detectChanges();
      try {
        await this.copilotCtx.runForAnalysis();
        await firstValueFrom(this.copilotSvc.refreshContext(this.sessionId!, {
          userId:        this._userId,
          currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
          columnNames:   this.columnNames,
          inputCacheKey: this.inputCacheKey,
          lastRunStats:  this.copilotCtx.context$.value?.lastRunStats,
        })).catch(() => {});
      } catch { /* proceed even if pre-run fails */ }
      this.activeToolName = null;
      this.cdr.detectChanges();
    }

    // Step 2: Copilot analysis stream
    const assistantMsgId      = uuidv4();
    this._streamingMessageId  = assistantMsgId;
    this._activeCellId        = assistantMsgId;
    this.messages.push({
      id: assistantMsgId, role: 'assistant', content: '',
      isStreaming: true, timestamp: new Date(), toolActivity: [],
    });
    this._scrollToBottom();

    this._streamSub?.unsubscribe();
    const liveConfig = this.copilotCtx.getLiveConfig?.() ?? this.currentConfig;
    this._streamSub  = this.copilotSvc.runFullAnalysis(
      this.sessionId!, this._userId, this.copilotSettings.depth, liveConfig,
    ).subscribe({
      next:     (event: SseEvent) => this._handleSseEvent(event, assistantMsgId),
      error:    (err) => {
        this._stopTimer();
        const wasStopping = this.isStopping;
        this._finaliseStreamingMessage(assistantMsgId, wasStopping ? '' : `Error: ${err?.error?.message ?? err?.message ?? err}`);
        this.isProcessing = false;
        this.isStopping   = false;
        this.cdr.detectChanges();
        if (wasStopping) {
          this._addStopMessage(assistantMsgId);
        }
      },
      complete: () => this._onStreamComplete(assistantMsgId),
    });
  }

  onRejectProposal(): void {
    if (!this.sessionId) return;
    this.copilotSvc.rejectProposal(this.sessionId, this._userId).subscribe();
    this.pendingProposal = null;
    this._appendMessage('user', 'I want to keep working on this — the proposal isn\'t quite right yet.');
    this._sendToBackend('The user rejected the proposal. Ask them what specifically needs to change.');
  }

  // ------------------------------------------------------------------
  // Post-save survey
  // ------------------------------------------------------------------

  toggleSurvey(): void {
    this.surveyEnabled = !this.surveyEnabled;
    localStorage.setItem(CopilotPanelComponent._SURVEY_PREF_KEY, String(this.surveyEnabled));
  }

  setQualityRating(rating: number): void       { this.surveyQualityRating      = rating; }
  setAccuracyRating(rating: number): void      { this.surveyAccuracyRating     = rating; }
  setSimplicityRating(rating: number): void    { this.surveySimplicityRating   = rating; }
  setCopilotSpeedRating(rating: number): void  { this.surveyCopilotSpeedRating = rating; }
  setDtrSpeedRating(rating: number): void      { this.surveyDtrSpeedRating     = rating; }
  setFrictionRating(rating: number): void      { this.surveyFrictionRating     = rating; }
  setTrustRating(rating: number): void         { this.surveyTrustRating        = rating; }

  submitSurvey(): void  { this._completeSurvey(true); }
  dismissSurvey(): void { this._completeSurvey(false); }

  setAnalysisInsightRating(rating: number): void       { this.analysisInsightRating       = rating; }
  setAnalysisAccuracyRating(rating: number): void      { this.analysisAccuracyRating      = rating; }
  setAnalysisActionabilityRating(rating: number): void { this.analysisActionabilityRating = rating; }
  setAnalysisGoalsUnderstandingRating(rating: number): void { this.analysisGoalsUnderstandingRating = rating; }
  setAnalysisTrustRating(rating: number): void         { this.analysisTrustRating         = rating; }

  submitAnalysisSurvey(): void  { this._completeAnalysisSurvey(true); }
  dismissAnalysisSurvey(): void { this._completeAnalysisSurvey(false); }

  private _completeAnalysisSurvey(submitted: boolean): void {
    if (!this.sessionId) {
      this.showAnalysisSurvey = false;
      return;
    }
    const payload: AnalysisCompletePayload = {
      wantsDescriptionAppend: this._wantsAnalysisDescriptionAppend,
      insightQualityRating:  submitted ? this.analysisInsightRating       : null,
      accuracyRating:        submitted ? this.analysisAccuracyRating      : null,
      actionabilityRating:   submitted ? this.analysisActionabilityRating : null,
      goalsUnderstandingRating: submitted ? this.analysisGoalsUnderstandingRating : null,
      trustRating:           submitted ? this.analysisTrustRating         : null,
      surveyFeedbackText:    submitted ? (this.analysisFeedbackText.trim() || null) : null,
    };

    let busyDialog: MatDialogRef<BusyIndicatorComponent> | null = null;
    if (this._wantsAnalysisDescriptionAppend) {
      // Same busy-dialog convention as the build survey — the Description summary is
      // a real LLM call, several seconds or more on a slow connection.
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.panelClass = 'busy-dialog';
      dialogConfig.data = { message: 'DTR Copilot is summarizing this analysis...' };
      busyDialog = this.matDialog.open(BusyIndicatorComponent, dialogConfig);
    }

    this.copilotSvc.notifyAnalysisComplete(this.sessionId, this._userId, payload).subscribe({
      next: (resp) => {
        busyDialog?.close();
        if (resp?.analystDescriptionAppend) {
          // See the update_description handler above for why this must be nested
          // under `config` rather than emitted at the top level.
          this.configPreviewUpdate.emit({ config: { analystDescriptionAppend: resp.analystDescriptionAppend } });
        }
      },
      error: () => busyDialog?.close(),
    });
    this.showAnalysisSurvey = false;
    this._wantsAnalysisDescriptionAppend = false;
    this.cdr.detectChanges();
  }

  private _completeSurvey(submitted: boolean): void {
    if (!this.sessionId || !this._pendingSavedEvent) {
      this.showSurvey = false;
      return;
    }
    // Copilot writes the session summary before responding (see work_complete in
    // copilot_blueprint.py) — that's a real LLM call, ~5s+ and longer on a slow connection.
    // Standard DTR busy dialog fills the gap rather than leaving the analyst looking at nothing.
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = 'busy-dialog';
    dialogConfig.data = { message: 'DTR Copilot is summarizing this session...' };
    const busyDialog: MatDialogRef<BusyIndicatorComponent> = this.matDialog.open(BusyIndicatorComponent, dialogConfig);

    this.copilotSvc.notifyWorkComplete(this.sessionId, this._userId, {
      ...this._pendingSavedEvent,
      surveyQualityRating:      submitted ? this.surveyQualityRating      : null,
      surveyAccuracyRating:     submitted ? this.surveyAccuracyRating     : null,
      surveySimplicityRating:   submitted ? this.surveySimplicityRating   : null,
      surveyCopilotSpeedRating: submitted ? this.surveyCopilotSpeedRating : null,
      surveyDtrSpeedRating:     submitted ? this.surveyDtrSpeedRating     : null,
      surveyFrictionRating:     submitted ? this.surveyFrictionRating     : null,
      surveyTrustRating:        submitted ? this.surveyTrustRating        : null,
      surveyFeedbackText:       submitted ? (this.surveyFeedbackText.trim() || null) : null,
      // Objective telemetry, not a survey response — sent regardless of submit/dismiss.
      dtrRunAllSeconds: this._lastDtrRunSeconds,
    }).subscribe({
      next: (resp) => {
        busyDialog.close();
        if (resp?.analystDescriptionAppend) {
          // See the update_description handler above for why this must be nested
          // under `config` rather than emitted at the top level.
          this.configPreviewUpdate.emit({ config: { analystDescriptionAppend: resp.analystDescriptionAppend } });
        }
      },
      error: () => busyDialog.close(),
    });
    this.showSurvey          = false;
    this._pendingSavedEvent  = null;
    this.cdr.detectChanges();
  }

  // ------------------------------------------------------------------
  // Mode switching
  // ------------------------------------------------------------------

  resetSession(): void {
    this._streamSub?.unsubscribe();
    this.copilotSvc.closeStream();
    if (this.sessionId) {
      this.copilotSvc.deleteSession(this.sessionId, this._userId).subscribe();
    }
    this.messages            = [];
    this.sessionId           = null;
    this.pendingProposal     = null;
    this.isProcessing        = false;
    this.activeToolName      = null;
    this.unreadCount         = 0;
    this._workDepth              = null;
    this.hasAuditableSession     = false;
    this.lastNavigationHint      = null;
    this.lastAnalysisPreviewData = null;
    this.showSurvey              = false;
    this.showAnalysisSurvey      = false;
    this.showAnalysisGoalsPrompt = false;
    this.showSummarizeAnalysisBtn = false;
    this._lastStreamWasAnalysis  = false;
    this._wantsAnalysisDescriptionAppend = false;
    this._hasCopilotConfigChange = false;
    this._pendingSavedEvent      = null;
    this._lastDtrRunSeconds      = null;
    this.advisorAdvice           = null;
    this.cdr.detectChanges();
    this._startSession();
  }

  // ── Unified advisor flow ──────────────────────────────────────────────────

  _showAdvisorAdvice(advice: any): void {
    if (!advice) return;
    this.advisorAdvice = advice;
    const isSample = !(this.copilotCtx.getLiveConfig?.() ?? this.currentConfig)?.runAll;

    if (advice.type === 'sort_offer') {
      const reason = advice.sortReason ?? 'performance';
      const baseMsg = reason === 'catch_all_misplaced'
        ? 'A catch-all rule is not last — it will claim all remaining rows and block every rule below it. Apply the correction?'
        : reason === 'anchor_dependency'
        ? 'Rule order correction needed — an anchor dependency conflict was detected. Apply the fix?'
        : 'I can optimise the rule execution order for better performance. Apply the new order?';
      const note = isSample ? ' Note: based on sample data — run with Run All Rows for the most accurate ordering.' : '';
      this._addSystemMessage(baseMsg + note, 'advisor_offer');
    } else if (advice.type === 'sample_range') {
      const msg = `The current sample range is **${Math.round((advice.representativeness || 0) * 100)}% representative** of the full dataset. Sorting by **${advice.recommended_sort}** and using range **${advice.recommended_start}–${advice.recommended_end}** will give a more accurate cross-section. Apply this change?`;
      this._addSystemMessage(msg, 'advisor_offer');
    } else if (advice.type === 'quality') {
      this._addSystemMessage(advice.summary ?? 'Quality assessment complete.', 'advisor_info');
      this.advisorAdvice = null;
      this._getNextAdvisorAdvice();
      return;
    }
    this.cdr.detectChanges();
  }

  onAdvisorAccept(): void {
    const advice = this.advisorAdvice;
    if (!advice) return;
    this.advisorAdvice = null;
    this.messages = this.messages.filter(m => m.subType !== 'advisor_offer');

    if (advice.type === 'sort_offer' && advice.sortedConfig?.rules?.length && this.transformId) {
      this.copilotCtx.rulesSorted$.next({ rules: advice.sortedConfig.rules, transformId: this.transformId, userInitiated: true });
    } else if (advice.type === 'sample_range') {
      this.copilotCtx.applySampleRange({
        sampleStart: advice.recommended_start,
        sampleEnd:   advice.recommended_end,
        sortBy:      advice.recommended_sort,
      });
    }
    this._getNextAdvisorAdvice();
  }

  onAdvisorSkip(): void {
    this.advisorAdvice = null;
    this.messages = this.messages.filter(m => m.subType !== 'advisor_offer');
    this.cdr.detectChanges();
    this._getNextAdvisorAdvice();
  }

  private _getNextAdvisorAdvice(): void {
    if (!this.sessionId) return;
    this.copilotSvc.advisorNext(this.sessionId, this._userId).subscribe({
      next: (resp) => {
        if (resp?.advisorFlow) {
          this._showAdvisorAdvice(resp.advisorFlow);
        }
      },
    });
  }

  setMode(newMode: CopilotMode): void {
    if (!this.sessionId || newMode === this.mode) return;
    this.copilotSvc.setMode(this.sessionId, this._userId, newMode).subscribe({
      next: () => {
        this.mode = newMode;
        this.copilotSettings.setMode(newMode);
        this._addSystemMessage(`Switched to ${newMode.charAt(0).toUpperCase() + newMode.slice(1)} mode.`);
      },
    });
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  private _appendMessage(role: 'user' | 'assistant', content: string): void {
    this.messages.push({
      id: uuidv4(),
      role,
      content,
      isStreaming: false,
      timestamp: new Date(),
    });
    if (!this.isExpanded) this.unreadCount++;
    this.cdr.detectChanges();
    this._scrollToBottom();
  }

  private _appendInsightMessage(insight: CopilotInsight): void {
    this.messages.push({
      id: uuidv4(),
      role: 'assistant',
      content: insight.message,
      isStreaming: false,
      timestamp: new Date(),
      insight,
    });
    this.cdr.detectChanges();
    this._scrollToBottom();
  }

  private _addSystemMessage(content: string, subType?: string): void {
    this.messages.push({
      id: uuidv4(),
      role: 'system' as any,
      content,
      isStreaming: false,
      timestamp: new Date(),
      subType,
    });
    this.cdr.detectChanges();
    this._scrollToBottom();
  }

  private _stopTimer(): void {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
  }

  private _buildStopSummary(msgId: string): string {
    const msg  = this.messages.find(m => m.id === msgId);
    const done = (msg?.toolActivity ?? []).filter(t => t.phase === 'done');
    if (done.length === 0) return '';
    const counts = new Map<string, number>();
    done.forEach(t => counts.set(t.name, (counts.get(t.name) ?? 0) + 1));
    const parts = [...counts.entries()].map(([n, c]) =>
      c > 1 ? `${n.replace(/_/g, ' ')} ×${c}` : n.replace(/_/g, ' '),
    );
    return `Completed: ${parts.join(', ')}.`;
  }

  private _addStopMessage(msgId: string): void {
    this._queuedMessage = null;
    const summary = this._buildStopSummary(msgId);
    this._addSystemMessage(
      summary
        ? `Stopped. ${summary} Say "Resume" to continue, or send a new request.`
        : 'Stopped. Say "Resume" to continue, or send a new request.',
    );
  }

  get processingTime(): string {
    const m = Math.floor(this.processingSeconds / 60);
    const s = this.processingSeconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  private _scrollToBottom(): void {
    setTimeout(() => {
      const el = this.chatWindow?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  private get _userId(): string {
    return this.userDataSvc.getUserId() ?? 'unknown';
  }

  private get _orgId(): string {
    return this.userDataSvc.getOrgId() ?? '0';
  }

  // Template helpers
  get hasQueuedMessage(): boolean { return !!this._queuedMessage; }

  get lastAssistantMessageId(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].role === 'assistant' && !this.messages[i].isStreaming) {
        return this.messages[i].id;
      }
    }
    return null;
  }

  onRunAuditClick(): void {
    this.runAuditRequested.emit();
  }

  /** True when there are active rules in the live config and no analysis has run yet. */
  get canAnalyzeConfig(): boolean {
    if (this.showAnalysisGoalsPrompt) return false;
    const config = this.copilotCtx.getLiveConfig?.() ?? this.currentConfig;
    const rules  = (config?.rules ?? []) as any[];
    return rules.some((r: any) => r.run !== false);
  }

  // DTR Copilot Config Analyser — goals-source choice. Resolved as a real, deterministic
  // Angular UI element rather than an LLM-authored checkpoint(choices=...) call: an earlier
  // version asked the LLM to pose this as a choice-bearing checkpoint, but nothing stopped
  // it from free-narrating the config first (mimicking the build flow's own multi-cell
  // convention) and it did not reliably populate `choices`, leaving a plain-text question
  // with no buttons. Resolving the choice client-side, before any LLM turn starts, removes
  // that failure mode entirely.
  showAnalysisGoalsPrompt = false;
  analysisGoalsInput = '';
  // Set once an analysis narration stream finishes — offers a real, deterministic button
  // instead of relying on the LLM to author a checkpoint(choices=...) call, for the same
  // reason the goals-source choice above was moved client-side: it did not reliably render.
  showSummarizeAnalysisBtn = false;
  private _lastStreamWasAnalysis = false;

  async onAnalyzeExistingConfig(): Promise<void> {
    if (!this.sessionId || this.isProcessing) return;
    await this._runForAnalysisPreRun();
    // The pre-run is done — Copilot is now idle, waiting on the analyst's goals choice,
    // not still processing. Clear the processing indicator before showing the goals card
    // so the progress bar/timer doesn't keep counting while nothing is actually running.
    this._stopTimer();
    this.isProcessing   = false;
    this.activeToolName = null;
    this.showAnalysisGoalsPrompt = true;
    this.analysisGoalsInput = '';
    this.cdr.detectChanges();
    this._scrollToBottom();
  }

  inferAnalysisGoals(): void {
    this.showAnalysisGoalsPrompt = false;
    this._beginConfigAnalysis(null);
  }

  submitAnalysisGoals(): void {
    const goals = this.analysisGoalsInput.trim();
    if (!goals) return;
    this.showAnalysisGoalsPrompt = false;
    this._beginConfigAnalysis(goals);
  }

  private async _runForAnalysisPreRun(): Promise<void> {
    this.isProcessing      = true;
    this.activeToolName    = null;
    this.processingSeconds = 0;
    this._timerInterval    = setInterval(
      () => { this.processingSeconds++; this.cdr.detectChanges(); }, 1000);

    // Run .NET DTR first — same as onRunFullAnalysis() — to populate the Results Dashboard.
    if (this.copilotCtx.runForAnalysis) {
      this.activeToolName = 'dtr_run';
      this.cdr.detectChanges();
      try {
        await this.copilotCtx.runForAnalysis();
        await firstValueFrom(this.copilotSvc.refreshContext(this.sessionId!, {
          userId:        this._userId,
          currentConfig: this.copilotCtx.getLiveConfig?.() ?? this.currentConfig,
          columnNames:   this.columnNames,
          inputCacheKey: this.inputCacheKey,
          lastRunStats:  this.copilotCtx.context$.value?.lastRunStats,
        })).catch(() => {});
      } catch { /* proceed even if pre-run fails */ }
      this.activeToolName = null;
      this.cdr.detectChanges();
    }
  }

  summarizeAnalysisIntoDescription(): void {
    this.showSummarizeAnalysisBtn = false;
    this._wantsAnalysisDescriptionAppend = true;
    this.showAnalysisSurvey = true;
    this.analysisInsightRating       = null;
    this.analysisAccuracyRating      = null;
    this.analysisActionabilityRating = null;
    this.analysisGoalsUnderstandingRating = null;
    this.analysisTrustRating         = null;
    this.analysisFeedbackText        = '';
    this.cdr.detectChanges();
  }

  private _beginConfigAnalysis(analysisGoals: string | null): void {
    // Restart the processing indicator — cleared after the pre-run so it didn't sit
    // counting while the goals card waited on the analyst; the real LLM turn starts now.
    this._lastStreamWasAnalysis = true;
    this.isProcessing      = true;
    this.activeToolName    = null;
    this.processingSeconds = 0;
    this._timerInterval    = setInterval(
      () => { this.processingSeconds++; this.cdr.detectChanges(); }, 1000);

    const assistantMsgId     = uuidv4();
    this._streamingMessageId = assistantMsgId;
    this._activeCellId       = assistantMsgId;
    this.messages.push({
      id: assistantMsgId, role: 'assistant', content: '',
      isStreaming: true, timestamp: new Date(), toolActivity: [],
    });
    this._scrollToBottom();

    this._streamSub?.unsubscribe();
    const liveConfig = this.copilotCtx.getLiveConfig?.() ?? this.currentConfig;
    this._streamSub  = this.copilotSvc.analyzeExistingConfig(
      this.sessionId!, this._userId, liveConfig, analysisGoals,
    ).subscribe({
      next:     (event: SseEvent) => this._handleSseEvent(event, assistantMsgId),
      error:    (err) => {
        this._stopTimer();
        const wasStopping = this.isStopping;
        this._finaliseStreamingMessage(assistantMsgId, wasStopping ? '' : `Error: ${err?.error?.message ?? err?.message ?? err}`);
        this.isProcessing = false;
        this.isStopping   = false;
        this.cdr.detectChanges();
        if (wasStopping) {
          this._addStopMessage(assistantMsgId);
        }
      },
      complete: () => this._onStreamComplete(assistantMsgId),
    });
  }

  /** DTR Copilot Discovery Engine (Phase 1) — "Analyze Input Farm" button. Unlike
   * onAnalyzeExistingConfig(), there's no pre-run or goals prompt: discovery reports
   * on the input farm itself, independent of any config, so the turn starts immediately. */
  onDiscoverInputFarm(): void {
    if (!this.sessionId || this.isProcessing) return;

    this._lastStreamWasAnalysis = true;
    this.isProcessing      = true;
    this.activeToolName    = null;
    this.processingSeconds = 0;
    this._timerInterval    = setInterval(
      () => { this.processingSeconds++; this.cdr.detectChanges(); }, 1000);

    const assistantMsgId     = uuidv4();
    this._streamingMessageId = assistantMsgId;
    this._activeCellId       = assistantMsgId;
    this.messages.push({
      id: assistantMsgId, role: 'assistant', content: '',
      isStreaming: true, timestamp: new Date(), toolActivity: [],
    });
    this._scrollToBottom();

    this._streamSub?.unsubscribe();
    this._streamSub = this.copilotSvc.discoverInputFarm(this.sessionId, this._userId).subscribe({
      next:     (event: SseEvent) => this._handleSseEvent(event, assistantMsgId),
      error:    (err) => {
        this._stopTimer();
        const wasStopping = this.isStopping;
        this._finaliseStreamingMessage(assistantMsgId, wasStopping ? '' : `Error: ${err?.error?.message ?? err?.message ?? err}`);
        this.isProcessing = false;
        this.isStopping   = false;
        this.cdr.detectChanges();
        if (wasStopping) {
          this._addStopMessage(assistantMsgId);
        }
      },
      complete: () => this._onStreamComplete(assistantMsgId),
    });
  }

  issueLabels(msg: ChatMessage): CopilotLabel[] {
    return (msg.labels ?? []).filter(l => l.type === 'issue');
  }

  actionLabels(msg: ChatMessage): CopilotLabel[] {
    return (msg.labels ?? []).filter(l => l.type === 'decision' || l.type === 'option');
  }

  msgHasAnalysisActivity(msg: ChatMessage): boolean {
    // A cell_boundary event (see the 'cell_boundary' case above) finalizes the current
    // message and starts a fresh one for the next cell — a multi-cell build (e.g. BPM's
    // Phase 6 narration → final checkpoint) splits its tool activity across several
    // assistant messages, so the qualifying tool call is often NOT on this specific
    // (usually the last) message. Walk back through every assistant message to find it,
    // rather than checking msg alone.
    //
    // Deliberately does NOT stop at the preceding user message: confirmed live,
    // 2026-09-13, that stopping there hid Dashboard/Summary/Audit entirely on the very
    // next turn after a completed build — e.g. clicking "Accept" on the
    // update_description offer starts a new turn whose own tool activity is just
    // update_description, so the per-turn walk found nothing and hid buttons that were
    // still perfectly valid (the build itself didn't change). These buttons reflect the
    // CURRENT farm/config state, which persists across turns within a session — the
    // same scope hasAuditableSession (also required alongside this check) already
    // uses — so once a real build/analysis has happened anywhere in the session, they
    // should stay available regardless of how many follow-up turns came after it.
    const idx = this.messages.findIndex(m => m.id === msg.id);
    for (let i = idx; i >= 0; i--) {
      const m = this.messages[i];
      if ((m.toolActivity ?? []).some(
        t => t.name === 'analyze_and_run' || t.name === 'run_full_analysis'
          || t.name === 'analyze_existing_config' || t.name === 'build_rule'
          || t.name === 'discover_input_farm'
      )) return true;
    }
    return false;
  }

  get modeLabel(): string {
    return { builder: 'Builder', advisor: 'Advisor', autonomous: 'Autonomous ⚡' }[this.mode] ?? this.mode;
  }

  get modeColor(): string {
    return { builder: 'primary', advisor: 'accent', autonomous: 'warn' }[this.mode] ?? 'primary';
  }

  trackByMsgId(_: number, msg: ChatMessage): string { return msg.id; }
}
